/* ============================================
   QUOTE WORKSTATION — Replacement damaged-items picker
   Asset: workstation-replacement.js

   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21)

   Part of a delivery arrived damaged. The admin spawns a replacement order from
   the Deal Dashboard and lands here, and the very first question is the one this
   panel asks: WHICH items were damaged, and how many of each?

   ⛔ EVERY QUANTITY STARTS AT ZERO. Usually only part of a shipment breaks. If the
   replacement arrived pre-filled with the whole original order, the admin's job
   would be to remember to trim it — and a miss ships a second complete order to
   a customer who never asked for one. Starting at zero inverts the failure: a
   mistake UNDER-orders, which someone notices and fixes cheaply.

   (It is also how it has to work mechanically — Shopify will not accept a draft
   order line item with quantity 0 — but the safety argument is the real reason.)

   The parent's lines arrive as `wm.replacementCandidates`, built server-side by
   joining the original Shopify draft (which owns QUANTITY) with its
   workstation_meta (which owns COST). See handleCreateReplacementOrder.

   Public API on window.QWS:
     renderReplacementPicker()  — draw/refresh the panel (no-op when not a replacement)
   ============================================ */
(function () {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;

  var state = Q.state;
  var wrap = Q.wrap;
  var escHtml = Q.escHtml;
  var escAttr = Q.escAttr;
  var fmt = Q.fmt;

  var PANEL_CLASS = 'rq-ws-replacement-picker';

  var REASON_LABELS = {
    'carrier-damage': 'Damaged in transit',
    'vendor-defect': 'Vendor defect / DOA',
    'wrong-item': 'Wrong item shipped',
    'warranty': 'Warranty replacement',
    'goodwill': 'Goodwill / our error'
  };

  /* How many of this candidate are already on the replacement, matched the same way
     the server matches wm.items — variant id first, then title. Lets the panel show
     "2 of 6 added" and stops a second click from double-adding. */
  function alreadyOnOrder(cand) {
    var n = 0;
    (state.items || []).forEach(function (it) {
      var same = (cand.variantId && it.variantId === cand.variantId)
        || (!cand.variantId && it.title === cand.title);
      if (same) n += (parseInt(it.quantity, 10) || 0);
    });
    return n;
  }

  function panelHtml() {
    var cands = state.replacementCandidates || [];
    var reason = REASON_LABELS[state.replacementReason] || 'Replacement';
    var parent = state.replacementParentRef || '';

    var h = '<div class="' + PANEL_CLASS + '__head">' +
      '<div>' +
        '<div class="' + PANEL_CLASS + '__title">↩ Which items were damaged?</div>' +
        '<div class="' + PANEL_CLASS + '__sub">' +
          escHtml(reason) + ' on <strong>' + escHtml(parent) + '</strong>. ' +
          'Set a quantity for each broken item, then add them. Everything here is <strong>$0 to the customer</strong> — ' +
          'the costs shown are ours, and they are what the supplier PO gets built from.' +
        '</div>' +
      '</div>' +
      '<button type="button" class="' + PANEL_CLASS + '__toggle">Hide</button>' +
    '</div>';

    if (!cands.length) {
      h += '<p class="' + PANEL_CLASS + '__empty">The original order had no goods lines to replace. ' +
        'Add the items by hand using the search bar below.</p>';
      return h;
    }

    h += '<table class="' + PANEL_CLASS + '__table"><thead><tr>' +
      '<th>Item</th><th>Vendor</th><th class="num">Ordered</th><th class="num">Our cost</th>' +
      '<th class="num">Damaged</th><th></th></tr></thead><tbody>';

    cands.forEach(function (c, i) {
      var on = alreadyOnOrder(c);
      h += '<tr data-cand-idx="' + i + '">' +
        '<td><span class="' + PANEL_CLASS + '__name">' + escHtml(c.title || 'Item') + '</span>' +
          (c.sku ? '<span class="' + PANEL_CLASS + '__sku">' + escHtml(c.sku) + '</span>' : '') + '</td>' +
        '<td>' + escHtml(c.vendor || '—') + '</td>' +
        '<td class="num">' + (c.parentQty || 0) + '</td>' +
        '<td class="num">' + (c.cost ? fmt(c.cost) : '—') + '</td>' +
        '<td class="num"><input type="number" class="' + PANEL_CLASS + '__qty" min="0" step="1" ' +
          'max="' + escAttr(String(c.parentQty || 0)) + '" value="0" data-cand-idx="' + i + '"></td>' +
        '<td class="num">' + (on > 0
          ? '<span class="' + PANEL_CLASS + '__on">' + on + ' added</span>'
          : '') + '</td>' +
      '</tr>';
    });

    h += '</tbody></table>' +
      '<div class="' + PANEL_CLASS + '__actions">' +
        '<button type="button" class="' + PANEL_CLASS + '__add">Add damaged items</button>' +
        '<button type="button" class="' + PANEL_CLASS + '__all">Everything was damaged</button>' +
        '<span class="' + PANEL_CLASS + '__msg" aria-live="polite"></span>' +
      '</div>';
    return h;
  }

  function ensurePanel() {
    var section = wrap.querySelector('.rq-ws-section--items');
    if (!section) return null;
    var panel = section.querySelector('.' + PANEL_CLASS);
    if (!panel) {
      panel = document.createElement('div');
      panel.className = PANEL_CLASS;
      section.insertBefore(panel, section.firstChild);
      wirePanel(panel);
    }
    return panel;
  }

  function wirePanel(panel) {
    panel.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('.' + PANEL_CLASS + '__toggle')) {
        panel.classList.toggle(PANEL_CLASS + '--collapsed');
        var btn = panel.querySelector('.' + PANEL_CLASS + '__toggle');
        if (btn) btn.textContent = panel.classList.contains(PANEL_CLASS + '--collapsed') ? 'Show' : 'Hide';
        return;
      }
      if (t.closest('.' + PANEL_CLASS + '__all')) {
        panel.querySelectorAll('.' + PANEL_CLASS + '__qty').forEach(function (inp) {
          inp.value = inp.getAttribute('max') || '0';
        });
        return;
      }
      if (t.closest('.' + PANEL_CLASS + '__add')) { addSelected(panel); return; }
    });
  }

  function addSelected(panel) {
    var cands = state.replacementCandidates || [];
    var msg = panel.querySelector('.' + PANEL_CLASS + '__msg');
    var added = 0;

    panel.querySelectorAll('.' + PANEL_CLASS + '__qty').forEach(function (inp) {
      var qty = parseInt(inp.value, 10) || 0;
      if (qty <= 0) return;
      var c = cands[parseInt(inp.dataset.candIdx, 10)];
      if (!c) return;
      /* Clamp to what was actually ordered. Replacing more than we sold is either a
         typo or a different conversation entirely, and it would land on a supplier PO. */
      var cap = parseInt(c.parentQty, 10) || 0;
      if (cap > 0 && qty > cap) qty = cap;

      /* ⛔ points/directPrice deliberately absent: addItem only auto-prices when it has
         a cost AND a mapPrice (or points), so passing neither leaves this at $0 — which
         is what a replacement is. The server coerces it to 0 again on save regardless. */
      Q.addItem({
        type: c.variantId ? 'product' : 'custom',
        variantId: c.variantId || null,
        vendor: c.vendor || '',
        title: c.title || 'Item',
        sku: c.sku || '',
        quantity: qty,
        cost: c.cost || 0,
        subDescriptions: (c.subDescriptions || []).slice(),
        unitMode: c.unitMode || 'piece'
      });
      added += qty;
      inp.value = '0';
    });

    if (!added) {
      if (msg) msg.textContent = 'Set a quantity above 0 on at least one item first.';
      return;
    }
    if (msg) msg.textContent = '✓ Added ' + added + ' item' + (added === 1 ? '' : 's') + ' — save when the list looks right.';
    Q.renderItems();
    Q.recalcTotals();
    renderReplacementPicker();   // refresh the "n added" column
  }

  function renderReplacementPicker() {
    if (!state.isReplacement) {
      var stale = wrap.querySelector('.' + PANEL_CLASS);
      if (stale) stale.remove();
      return;
    }
    var panel = ensurePanel();
    if (!panel) return;
    var wasCollapsed = panel.classList.contains(PANEL_CLASS + '--collapsed');
    panel.innerHTML = panelHtml();
    if (wasCollapsed) {
      panel.classList.add(PANEL_CLASS + '--collapsed');
      var btn = panel.querySelector('.' + PANEL_CLASS + '__toggle');
      if (btn) btn.textContent = 'Show';
    }
  }

  Q.renderReplacementPicker = renderReplacementPicker;

  /* ============================================================
     NEW REPLACEMENT ORDER — the Workstation's door (Ryan 2026-08-22)

     The Deal Dashboard cog was the only way to start a replacement. This is a second
     door for when you are already in the Workstation, and it is ONLY a door: it collects
     the parent ref + reason and POSTs the SAME /create-replacement-order the cog does.

     ⛔ WHY NOT A 4th "Replacement Deal" IN THE DEAL TYPE DROPDOWN, which is what was
     first proposed:
       · Deal Type IS `leadSource`, the COMMISSION field — inbound vs outbound picks the
         rep's rate, shared pays a partner a cut of GP. A replacement pays nobody, and
         handleSaveWorkstation deliberately writes no `leadSource:` tag on one. A 4th value
         puts a non-commission answer in the field every commission reader consults.
       · It would be a SECOND SOURCE OF TRUTH. `isReplacementRef(ref)` is deliberately THE
         test — ref-first, because the ref is immutable and a dropdown is one careless
         click away from re-classifying a sold deal.
       · The ref cannot change after the first save. From-scratch mints {INITIALS}-{n}-A;
         a replacement needs RPL-{n}-A off a global counter. Flipping a dropdown afterwards
         could only mint a second record and orphan the first.
       · And the flow lands where the cog already starts: the moment you name the parent,
         the only useful next step is "which of ITS lines broke?" — a list built
         server-side from the parent's draft joined to its costs.

     ⛔ THE GUARDS MUST MATCH THE COG'S, or this door reopens what the audit closed:
     no replacement-of-a-replacement, and no ONLINE order (it has no draft, so
     findDraftOrderByRef can never match and the route always 404s).
     ============================================================ */
  var API = Q.API;
  var replModal = wrap.querySelector('#rq-ws-repl-modal');

  /* ⛔ Keys must match the worker's REPLACEMENT_REASONS (lib/replacement-order.js) and the
     Deal Dashboard's REPLACEMENT_REASON_OPTIONS. Drift fails LOUDLY rather than silently —
     handleCreateReplacementOrder rejects an unknown key with "Unknown replacement reason".
     `chase` is what the reason actually decides: who we go after for the money. */
  var REPL_REASONS = [
    { key: 'carrier-damage', label: 'Damaged in transit', chase: 'We re-ship now and file a freight claim against the carrier in the background.' },
    { key: 'vendor-defect', label: 'Vendor defect / DOA', chase: 'Arrived faulty from the supplier. We re-ship and chase the vendor for credit.' },
    { key: 'wrong-item', label: 'Wrong item shipped', chase: 'The supplier sent the wrong goods. We re-ship the right ones and chase the vendor.' },
    { key: 'warranty', label: 'Warranty replacement', chase: 'In-warranty failure after delivery. We re-ship and claim against the vendor warranty.' },
    { key: 'goodwill', label: 'Goodwill / our error', chase: 'Our mistake, or a gesture. We eat the cost — no claim is tracked.' }
  ];

  var REPL_REF_RE = /^[A-Z]{2,4}-\d+-[A-Z]$/;      // the shape every ref family shares
  var REPL_IS_RPL_RE = /^RPL-\d+-[A-Z]$/;          // mirrors the worker's isReplacementRef

  if (replModal) (function () {
    var openBtn = wrap.querySelector('.rq-ws-bar__btn--replacement');
    var refInput = replModal.querySelector('.rq-ws-repl-modal__ref');
    var suggest = replModal.querySelector('.rq-ws-repl-modal__suggest');
    var matchEl = replModal.querySelector('.rq-ws-repl-modal__match');
    var reasonSel = replModal.querySelector('.rq-ws-repl-modal__reason');
    var reasonNote = replModal.querySelector('.rq-ws-repl-modal__reasonnote');
    var errEl = replModal.querySelector('.rq-ws-repl-modal__err');
    var goBtn = replModal.querySelector('.rq-ws-repl-modal__go');

    /* ⛔ In-flight lives HERE, not on the button — same reason the Deal Dashboard keeps it
       off the cog: a disabled attribute dies on the next render, the POST does not, and a
       second press mints a second replacement order. */
    var creating = false;
    var activeIdx = -1;
    var matched = null;   // the quotesCache row for the typed ref, when we have one

    REPL_REASONS.forEach(function (r) {
      var o = document.createElement('option');
      o.value = r.key;
      o.textContent = r.label;
      reasonSel.appendChild(o);
    });

    function reasonFor(key) {
      for (var i = 0; i < REPL_REASONS.length; i++) if (REPL_REASONS[i].key === key) return REPL_REASONS[i];
      return null;
    }

    function open() {
      if (Q.isAdmin === false) return;
      refInput.value = '';
      reasonSel.value = '';
      matched = null;
      activeIdx = -1;
      matchEl.textContent = '';
      matchEl.className = 'rq-ws-repl-modal__match';
      reasonNote.textContent = 'Pick what went wrong — it decides who we chase for the money.';
      errEl.textContent = '';
      suggest.hidden = true;
      goBtn.disabled = true;
      goBtn.textContent = '↩ Create replacement';
      replModal.style.display = '';
      /* The list is only fetched when the Load dropdown is opened, so it is usually empty
         on a fresh page. Warm it now so type-ahead works on the first keystroke — but the
         modal is fully usable without it (see refState: an unknown ref is ALLOWED through,
         because the cache is only the most recent 200 deals and the server is the
         authority on whether an order exists). */
      if (!state.quotesCache && Q.loadQuotesList) { try { Q.loadQuotesList(); } catch (e) {} }
      setTimeout(function () { refInput.focus(); }, 30);
    }

    function close() {
      if (creating) return;   // never yank the dialog out from under a live POST
      replModal.style.display = 'none';
      suggest.hidden = true;
    }

    function candidates(term) {
      var all = state.quotesCache || [];
      var t = term.toLowerCase();
      var out = [];
      for (var i = 0; i < all.length && out.length < 6; i++) {
        var q = all[i];
        var ref = String(q.reference || q.ref || '');
        if (!ref) continue;
        if (REPL_IS_RPL_RE.test(ref.toUpperCase())) continue;   // never offer a replacement
        if (q.source === 'online') continue;                    // no draft to read — same gate as the cog
        if (q.isPending) continue;                              // a pending has no order behind it
        var hay = (ref + ' ' + (q.customerCompany || '') + ' ' + (q.customer || '')).toLowerCase();
        if (t && hay.indexOf(t) === -1) continue;
        out.push(q);
      }
      return out;
    }

    function rowFor(ref) {
      var all = state.quotesCache || [];
      for (var i = 0; i < all.length; i++) {
        if (String(all[i].reference || all[i].ref || '').toUpperCase() === ref) return all[i];
      }
      return null;
    }

    /* One place decides whether the typed ref may be submitted, and what to say about it.
       Returns { ok, cls, msg }. Anything the CACHE cannot settle resolves to ok:true with a
       hedged message — the server is the authority, and blocking on a cache miss would
       refuse perfectly real orders older than the last 200. */
    function refState() {
      var ref = (refInput.value || '').trim().toUpperCase();
      if (!ref) return { ok: false, cls: '', msg: '' };
      if (!REPL_REF_RE.test(ref)) {
        return { ok: false, cls: 'bad', msg: 'Refs look like JW-1129-F.' };
      }
      if (REPL_IS_RPL_RE.test(ref)) {
        return { ok: false, cls: 'bad', msg: '✗ ' + ref + ' is itself a replacement — you cannot replace a replacement.' };
      }
      var row = rowFor(ref);
      matched = row;
      if (!row) {
        return { ok: true, cls: '', msg: 'Not in your recent deals — we’ll check when you create it.' };
      }
      if (row.source === 'online') {
        return { ok: false, cls: 'bad', msg: '✗ ' + ref + ' is an online order. Replacing one is a real need but is not built yet.' };
      }
      if (row.isPending) {
        return { ok: false, cls: 'bad', msg: '✗ ' + ref + ' is a pending lead, not an order — there is nothing shipped to replace.' };
      }
      var who = row.customerCompany || row.customer || 'Unknown customer';
      var st = row.lifecycleStatus ? String(row.lifecycleStatus) : '';
      return {
        ok: true, cls: 'ok',
        msg: '✓ ' + who + (row.customerCompany && row.customer ? ' · ' + row.customer : '') + (st ? ' · ' + st : '')
      };
    }

    function repaint() {
      var s = refState();
      matchEl.textContent = s.msg;
      matchEl.className = 'rq-ws-repl-modal__match' + (s.cls ? ' rq-ws-repl-modal__match--' + s.cls : '');
      goBtn.disabled = creating || !s.ok || !reasonSel.value;
      if (!creating) errEl.textContent = '';
    }

    function paintSuggest() {
      var term = (refInput.value || '').trim();
      var list = candidates(term);
      /* An exact hit needs no menu — the match line beneath already confirms it, and a
         dropdown covering the Reason field at that moment is pure obstruction. */
      if (!list.length || (list.length === 1 && String(list[0].reference || '').toUpperCase() === term.toUpperCase())) {
        suggest.hidden = true;
        activeIdx = -1;
        return;
      }
      var h = '';
      list.forEach(function (q, i) {
        var ref = String(q.reference || q.ref || '');
        var who = q.customerCompany || q.customer || '';
        h += '<button type="button" class="rq-ws-repl-modal__opt" data-ref="' + escAttr(ref) + '" data-idx="' + i + '">' +
          '<span class="rq-ws-repl-modal__opt-ref">' + escHtml(ref) + '</span>' +
          '<span class="rq-ws-repl-modal__opt-who">' + escHtml(who) + '</span>' +
          '</button>';
      });
      suggest.innerHTML = h;
      suggest.hidden = false;
      activeIdx = -1;
    }

    function choose(ref) {
      refInput.value = ref;
      suggest.hidden = true;
      activeIdx = -1;
      repaint();
      reasonSel.focus();
    }

    async function submit() {
      if (creating) return;
      var s = refState();
      if (!s.ok || !reasonSel.value) return;
      var ref = (refInput.value || '').trim().toUpperCase();
      creating = true;
      goBtn.disabled = true;
      goBtn.textContent = 'Creating…';
      errEl.textContent = '';
      try {
        var res = await API.post('/create-replacement-order', { ref: ref, reason: reasonSel.value });
        /* Straight into the new replacement: the next job is choosing which items were
           damaged, and the record is useless until that happens.
           ⛔ res.ref is preferred over res.redirectUrl here, unlike on the Deal Dashboard.
           The route's redirectUrl carries `&from=dashboard`, which relabels the Back button
           "← Back to Dashboard" (workstation-boot.js) — true for the cog, a lie for this
           door. redirectUrl stays as the fallback in case the route ever stops echoing ref. */
        if (res && res.ref) { window.location.href = window.location.pathname + '?ref=' + encodeURIComponent(res.ref); return; }
        if (res && res.redirectUrl) { window.location.href = res.redirectUrl; return; }
        throw new Error('The order was created but the portal did not say which ref — check the Deal Dashboard.');
      } catch (err) {
        /* Stay open with the error visible. The ref and reason are still typed, so a
           fixable mistake ("Order not found") costs one edit rather than a re-open. */
        creating = false;
        goBtn.textContent = '↩ Create replacement';
        errEl.textContent = (err && err.message) ? err.message : 'Could not create the replacement order.';
        repaint();
      }
    }

    if (openBtn) {
      /* Admin-only, mirroring the route's 403 and the Deal Dashboard cog. isAdmin is
         resolved at boot; if it is not known yet, leave the button hidden rather than
         flash an action the rep cannot use. */
      if (Q.isAdmin) openBtn.style.display = '';
      openBtn.addEventListener('click', open);
    }
    replModal.querySelector('.rq-ws-repl-modal__close').addEventListener('click', close);
    replModal.querySelector('.rq-ws-repl-modal__cancel').addEventListener('click', close);
    replModal.querySelector('.rq-ws-repl-modal__backdrop').addEventListener('click', close);
    goBtn.addEventListener('click', submit);

    refInput.addEventListener('input', function () {
      var caret = refInput.selectionStart;
      refInput.value = refInput.value.toUpperCase();
      try { refInput.setSelectionRange(caret, caret); } catch (e) {}
      paintSuggest();
      repaint();
    });
    refInput.addEventListener('focus', paintSuggest);
    refInput.addEventListener('keydown', function (e) {
      var opts = suggest.hidden ? [] : suggest.querySelectorAll('.rq-ws-repl-modal__opt');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!opts.length) return;
        e.preventDefault();
        activeIdx = e.key === 'ArrowDown'
          ? (activeIdx < opts.length - 1 ? activeIdx + 1 : 0)
          : (activeIdx > 0 ? activeIdx - 1 : opts.length - 1);
        opts.forEach(function (el, i) { el.classList.toggle('rq-ws-repl-modal__opt--active', i === activeIdx); });
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (activeIdx >= 0 && opts[activeIdx]) { choose(opts[activeIdx].dataset.ref); return; }
        if (!goBtn.disabled) submit();
        return;
      }
      if (e.key === 'Escape') { if (!suggest.hidden) { suggest.hidden = true; activeIdx = -1; } else close(); }
    });
    suggest.addEventListener('click', function (e) {
      var opt = e.target.closest('.rq-ws-repl-modal__opt');
      if (opt) choose(opt.dataset.ref);
    });
    reasonSel.addEventListener('change', function () {
      var r = reasonFor(reasonSel.value);
      reasonNote.textContent = r ? r.chase : 'Pick what went wrong — it decides who we chase for the money.';
      repaint();
    });
    replModal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && e.target !== refInput) close();
    });

    Q.openNewReplacement = open;
  })();
})();
